import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTranslation } from 'react-i18next';

const { FiMail, FiPhone, FiMapPin, FiInstagram, FiFacebook, FiAward } = FiIcons;

const Contact = () => {
  const { t } = useTranslation();

  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://link.msgsndr.com/js/form_embed.js";
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const contactInfo = [
    {
      icon: FiMail,
      title: t('Email'),
      content: 'info@castellomanagement.it',
      link: 'mailto:info@castellomanagement.it'
    },
    {
      icon: FiPhone,
      title: t('Telefono'),
      content: '+39 335 834 4557',
      link: 'tel:+393358344557'
    },
    {
      icon: FiMapPin,
      title: t('Sede Centrale'),
      content: 'Via Ravizza 14, 20149 Milano',
      link: 'https://maps.google.com/?q=Via+Ravizza+14+20149+Milano'
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="bg-brand-blue py-20 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-gold/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="flex justify-center mb-10">
              <img 
                src="https://i.imghippo.com/files/DqZX5957Q.png" 
                alt="Castello SRL" 
                className="h-24 w-auto brightness-0 invert" 
              />
            </div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-black mb-6 uppercase tracking-tight"
            >
              {t('Contattaci')}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-brand-silver mb-8 max-w-3xl mx-auto font-light"
            >
              Il team di Castello SRL è a tua disposizione per una consulenza strategica personalizzata sul tuo immobile.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Left Column: Info */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-brand-blue mb-8 uppercase tracking-widest text-sm">Informazioni Corporate</h2>
              <div className="space-y-8 mb-12">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-6">
                    <div className="bg-brand-blue w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <SafeIcon icon={info.icon} className="h-6 w-6 text-brand-gold" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-brand-blue mb-1">{info.title}</h3>
                      {info.link ? (
                        <a href={info.link} target="_blank" rel="noopener noreferrer" className="text-brand-subtle hover:text-brand-gold transition-colors text-lg">
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-brand-subtle text-lg">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-brand-blue/5 p-8 rounded-3xl border border-brand-gold/20">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-brand-gold p-2 rounded-lg">
                    <SafeIcon icon={FiAward} className="text-white h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-blue uppercase tracking-tight">Sede Legale Castello SRL</h3>
                </div>
                <p className="text-brand-subtle leading-relaxed">
                  Via Ravizza 14, 20149 Milano (MI)<br/>
                  P.IVA: 10257200963<br/><br/>
                  Il nostro team di esperti coordina ogni operazione per garantire l'eccellenza del servizio a Milano e in tutta Italia.
                </p>
              </div>
            </motion.div>

            {/* Right Column: Form */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="h-full"
            >
              <div className="bg-white p-10 rounded-3xl shadow-2xl border border-brand-silver/20 h-full relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-brand-gold"></div>
                <h2 className="text-3xl font-bold text-brand-blue mb-8 uppercase tracking-tight">Richiedi Informazioni</h2>
                
                <div className="form-container min-h-[500px]">
                  <iframe
                    src="https://api.leadconnectorhq.com/widget/form/nH1nVXTenURGs3xAczhr"
                    style={{ width: '100%', height: '100%', minHeight: '520px', border: 'none', borderRadius: '3px' }}
                    id="inline-nH1nVXTenURGs3xAczhr" 
                    data-layout="{'id':'INLINE'}"
                    data-trigger-type="alwaysShow"
                    data-trigger-value=""
                    data-activation-type="alwaysActivated"
                    data-activation-value=""
                    data-deactivation-type="neverDeactivate"
                    data-deactivation-value=""
                    data-form-name="FormInfoBase"
                    data-height="489"
                    data-layout-iframe-id="inline-nH1nVXTenURGs3xAczhr"
                    data-form-id="nH1nVXTenURGs3xAczhr"
                    title="FormInfoBase"
                  ></iframe>
                </div>
                
                <p className="mt-6 text-xs text-brand-subtle text-center">
                  Compilando il modulo verrai ricontattato da un nostro consulente entro 24 ore.
                </p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;